import { createObjectCsvStringifier } from 'csv-writer';
import { Edge, Node } from 'reactflow';
import { ReactFlowNodeData } from './types';

export class LineageExporter {
  private csvHeaders = [
    { id: 'filtered_asset_name', title: 'Filtered Asset Name' },
    { id: 'filtered_asset_path', title: 'Filtered Asset Path' },
    { id: 'direction', title: 'Direction' },
    { id: 'hops', title: 'Hops' },
    { id: 'type', title: 'Type' },
    { id: 'table_name', title: 'Table Name' },
    { id: 'dependent_asset_name', title: 'Name' },
    { id: 'dependent_asset_name', title: 'Dependent Asset Name' },
    { id: 'dependent_asset_path', title: 'Dependent Asset Path' },
    { id: 'dependent_asset_owners', title: 'Dependent Asset Owners' },
    { id: 'dependent_asset_tags', title: 'Dependent Asset Tags' },
  ];

  private nodesById: Record<string, Node<ReactFlowNodeData>> = {};

  private edgesBySource: Record<string, string[]> = {};

  private edgesByTarget: Record<string, string[]> = {};

  constructor(nodes: Node[], edges: Edge[]) {
    nodes.forEach((node) => {
      this.nodesById[node.id] = node;
    });
    edges.forEach((edge) => {
      this.edgesBySource[edge.source] = this.edgesBySource[edge.source] || [];
      this.edgesBySource[edge.source].push(edge.target);
      this.edgesByTarget[edge.target] = this.edgesByTarget[edge.target] || [];
      this.edgesByTarget[edge.target].push(edge.source);
    });
  }

  private resolveLineage(
    selectedNodeId: string,
    edgesMap: Record<string, string[]>,
  ) {
    const result: { node: Node; hops: number }[] = [];
    const visited = new Set();
    const { nodesById } = this;

    function findNodes(nodeId: string, hops: number) {
      if (!edgesMap[nodeId]) return;

      edgesMap[nodeId].forEach((edgeNode) => {
        if (!visited.has(edgeNode)) {
          visited.add(edgeNode);
          if (nodesById[edgeNode]) {
            result.push({ node: nodesById[edgeNode], hops });
          }
          findNodes(edgeNode, hops + 1);
        }
      });
    }

    findNodes(selectedNodeId, 1);

    return result;
  }

  private getNodePath(node: Node): string {
    const { metadata } = node.data;
    if (metadata.type === 'column') {
      const parentNode = this.nodesById[metadata.parent];
      return this.getNodePath(parentNode);
    }
    if (
      !!metadata.databaseName &&
      !!metadata.schemaName &&
      !!metadata.tableName
    ) {
      return `${metadata.databaseName}.${metadata.schemaName}.${metadata.tableName}`;
    }
    return metadata.path || metadata.label;
  }

  private getMetadata(node: Node): Record<string, string> {
    const metadata = node.data.parentNode || node.data.metadata;
    return {
      owners: metadata.owners ? metadata.owners.join(', ') : '',
      tags: metadata.tags ? metadata.tags.join(', ') : '',
    };
  }

  private resolvedLineageToCsvRecord(
    node: Node,
    lineage: { node: Node; hops: number },
    lineageDirection: 'upstream' | 'downstream',
  ): Record<string, any> {
    const lineageMetadata = this.getMetadata(lineage.node);
    return {
      id: lineage.node.id,
      parent_id: lineage.node.data.parentNode?.id,
      filtered_asset_name: node.data.metadata.label,
      filtered_asset_path: this.getNodePath(node),
      direction: lineageDirection,
      hops: lineage.hops,
      dependent_asset_name: lineage.node.data.metadata.label,
      dependent_asset_path: this.getNodePath(lineage.node),
      totals: lineage.node.data.totals,
      path:
        lineage.node.data.parentNode?.path || lineage.node.data.metadata.path,
      type:
        lineage.node.data.metadata.subType ||
        lineage.node.data.metadata.type ||
        'model',
      table_type: lineage.node.data.parentNode?.type,
      table_name: lineage.node.data.parentNode?.label,
      ...Object.keys(lineageMetadata).reduce((acc, key) => {
        acc[`dependent_asset_${key}`] = lineageMetadata[key];
        return acc;
      }, {} as Record<string, string>),
    };
  }

  private stringifyCsv(csvRecords: Record<string, any>[]): string {
    const stringifier = createObjectCsvStringifier({
      header: this.csvHeaders,
    });
    return (
      stringifier.getHeaderString() + stringifier.stringifyRecords(csvRecords)
    );
  }

  public exportLineage(nodeId?: string, stringifyCsv: boolean = true) {
    if (!nodeId) {
      return null;
    }
    const upstreamLineage = this.resolveLineage(nodeId, this.edgesByTarget);
    const downstreamLineage = this.resolveLineage(nodeId, this.edgesBySource);
    const node = this.nodesById[nodeId];
    const csvRecords = [
      ...downstreamLineage.map((lineage) =>
        this.resolvedLineageToCsvRecord(node, lineage, 'downstream'),
      ),
      ...upstreamLineage.map((lineage) =>
        this.resolvedLineageToCsvRecord(node, lineage, 'upstream'),
      ),
    ];

    return stringifyCsv ? this.stringifyCsv(csvRecords) : csvRecords;
  }
}
